import { createApp } from 'vue';
import { createI18n } from 'vue-i18n'
import router from './router';

// Third-party Imports
import VueVectorMap from 'vuevectormap';
import 'jsvectormap/dist/jsvectormap.css'
require('jsvectormap/dist/maps/world');

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Pinia Store
import { createPinia } from 'pinia';

// Lang Files
import en from './locales/global-en.json';

// Sentry
import * as Sentry from '@sentry/vue';

// PrimeVue Imports
import PrimeVue from 'primevue/config';
import { centralflexPreset } from './themes/themePreset'; // Custom theme preset
import Button from 'primevue/button';
import ToggleButton from 'primevue/togglebutton';
import SelectButton from 'primevue/selectbutton';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import Panel from 'primevue/panel';
import ContextMenu from 'primevue/contextmenu';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';
import AnimateOnScroll from 'primevue/animateonscroll';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
// PrimeVue Services
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';

// Custom Components
import ExampleComponent from './components/ExampleComponent.vue';
// Alerts
import AlertBannerComponent from './components/AlertBannerComponent.vue';
// Datatables
import DatatableComponent from './components/Datatables/DatatableComponent.vue';
import DynamicDatatableComponent from './components/Datatables/DynamicDatatableComponent.vue';
import DynamicNestedDatatableComponent from './components/Datatables/DynamicNestedDatatableComponent.vue';
// Whitelists
import WhitelistIndex from "./components/Whitelists/WhitelistIndex.vue";
import WhitelistView from "./components/Whitelists/WhitelistView.vue";
import WhitelistDetails from './components/Whitelists/components/WhitelistDetails.vue';
import WhitelistEntries from "./components/Whitelists/components/WhitelistEntries.vue";
import WhitelistMap from './components/Whitelists/components/WhitelistMap.vue';
import WhitelistCountries from "./components/Whitelists/components/WhitelistCountries.vue";


import {palette, updatePrimaryPalette} from "@primevue/themes";

// Localization
const i18n = createI18n({
    locale: 'en', // Default language
    messages: {
        en: en, // Use the loaded JSON as the translations for 'en'
    },
})

// Initialize the Vue Application
const app = createApp({
    components: {
        ExampleComponent,
        AlertBannerComponent,
        DatatableComponent,
        DynamicDatatableComponent,
        DynamicNestedDatatableComponent,
        WhitelistView,
        WhitelistDetails,
        WhitelistEntries,
        WhitelistCountries,
        WhitelistMap,
        WhitelistIndex
    },

    setup() {
        const themeSettings = window.ThemeSettings;
        updatePrimaryPalette(palette(themeSettings.themeColor)); // Update theme dynamically based on settings
    },

    mounted() {

    }
});

// Global Properties & Services Setup
app.config.globalProperties.$themeSettings = window.ThemeSettings;

// Global methods
app.config.globalProperties.$goBack = function () {
    this.$router.back();
};

// Create a Pinia store instance for state management
const pinia = createPinia();

// Initialize Sentry for error tracking
Sentry.init({
    app,
    dsn: process.env.SENTRY_DSN,
});

// Register Global Plugins and Services
app.use(PrimeVue, {
    ripple: true,
    theme: {
        preset: centralflexPreset, // Use the imported custom theme preset
        options: {
            prefix: 'p', // PrimeVue component prefix
            darkModeSelector: '.dark', // Dark mode selector class
            cssLayer: false, // Disable CSS layering if not needed
        },
    },
});

// Register Vectormap
app.use(VueVectorMap, {
    backgroundColor: '#f6f6f6', // Set global options for JSVectorMap
});

// Register Pinia
app.use(pinia);

// Register Services
app.use(ConfirmationService);
app.use(ToastService);

// Register PrimeVue Components
app.component('Button', Button);
app.component('ToggleButton', ToggleButton);
app.component('SelectButton', SelectButton);
app.component('InputText', InputText);
app.component('Card', Card);
app.component('Panel', Panel);
app.component('ContextMenu', ContextMenu);
app.component('IconField', IconField);
app.component('InputIcon', InputIcon);
app.component('MultiSelect', MultiSelect);
app.component('DatePicker', DatePicker);
app.component('ProgressSpinner', ProgressSpinner);
app.component('Tag', Tag);

// Register Directives
app.directive('animateonscroll', AnimateOnScroll);

// Register Vue I18n
app.use(i18n)
// Register Vue Router
app.use(router);
// Mount
app.mount('#app');
